import React from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

interface UpdateRequiredModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  appStoreUrl: string | null;
  platform: 'ios' | 'android' | 'web';
  currentVersion: string;
  minimumVersion: string;
}

export const UpdateRequiredModal: React.FC<UpdateRequiredModalProps> = ({
  isOpen,
  title,
  description,
  appStoreUrl,
  platform,
  currentVersion,
  minimumVersion
}) => {
  const handleOpenAppStore = async () => {
    if (!appStoreUrl) {
      console.error('[UpdateRequiredModal] No app store URL provided');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // Avoid hard dependency on @capacitor/app for web builds.
        window.location.href = appStoreUrl;
      } else {
        // Fallback for web
        window.open(appStoreUrl, '_blank');
      }
    } catch (error) {
      console.error('[UpdateRequiredModal] Error opening app store:', error);
      // Fallback to window.open
      window.open(appStoreUrl, '_blank');
    }
  };

  // Don't show modal if we're on web platform
  if (platform === 'web') {
    return null;
  }

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <AlertDialogTitle className="text-xl font-bold">
              {title}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3 pt-2">
            <p className="text-base">
              {description}
            </p>
            <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Mevcut Sürüm:</span>
                <span className="font-semibold">{currentVersion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gerekli Sürüm:</span>
                <span className="font-semibold text-orange-600 dark:text-orange-400">
                  {minimumVersion}
                </span>
              </div>
            </div>
            {!appStoreUrl && (
              <p className="text-sm text-muted-foreground italic">
                Lütfen uygulama mağazasından güncelleme yapın.
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2 mt-4">
          {appStoreUrl && (
            <Button
              onClick={handleOpenAppStore}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold"
              size="lg"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {platform === 'ios' ? 'App Store\'da Aç' : 'Play Store\'da Aç'}
            </Button>
          )}
          {!appStoreUrl && (
            <div className="text-center text-sm text-muted-foreground p-2">
              Uygulama mağazası bağlantısı yapılandırılmamış. Lütfen yönetici ile iletişime geçin.
            </div>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

